import type { User } from "@ronin/contracts";
import { DEFAULT_TIMEZONE, uuidv7 } from "@ronin/core";
import { api } from "../../api.js";
import { APP_VERSION } from "../../version.js";
import { serverUrl } from "../server.js";
import { nativeTokens } from "../tokens.js";
import { createCipher, deleteKey, loadKey } from "./cipher.js";
import { encryptedPersistence, indexedDbPersistence } from "./persistence.js";
import { createOfflineStore, type OfflineStore } from "./store.js";
import { createSync, type SyncResult } from "./sync.js";

/**
 * spec11: the Android app's offline notes. One store per server address and user (FR15),
 * opened after sign-in or, offline, from the device (FR16). Never used in the browser.
 */

let store: OfflineStore | null = null;
let timeZone = DEFAULT_TIMEZONE;
const storeListeners = new Set<() => void>();

function setStore(next: OfflineStore | null) {
  store = next;
  for (const listener of storeListeners) listener();
}

/** The open store (null in the browser and while signed out). */
export const offlineStore = () => store;

export function subscribeOfflineStore(listener: () => void) {
  storeListeners.add(listener);
  return () => void storeListeners.delete(listener);
}

/** Conflict copies are titled in the display timezone (FR8). */
export function setOfflineTimeZone(zone: string) {
  timeZone = zone;
}

/** The device's store, if a key exists (or `create`). Unreadable data is wiped. */
async function loadStore(create: boolean): Promise<OfflineStore | null> {
  const raw = await loadKey(create);
  if (!raw) return null;
  const persistence = encryptedPersistence(indexedDbPersistence(), await createCipher(raw));
  const loaded = createOfflineStore(persistence, { newId: uuidv7, timeZone: () => timeZone });
  try {
    await loaded.load();
  } catch {
    // Written with another key (or corrupted): it can never be read, so start over.
    await loaded.destroy();
  }
  return loaded;
}

/** The account the device's offline copy belongs to, and how many changes wait for it. */
export async function storedAccount(): Promise<{ userId: string; pending: number } | null> {
  const current = store ?? (await loadStore(false));
  const meta = current?.meta();
  return current && meta ? { userId: meta.user.id, pending: current.pendingCount() } : null;
}

/** After sign-in (and on every app start online): the store of this user on this server. */
export async function openOfflineStore(user: User): Promise<OfflineStore> {
  const meta = store?.meta();
  if (store && meta?.user.id === user.id && meta.serverUrl === serverUrl()) {
    if (meta.user.username !== user.username || meta.user.email !== user.email) {
      store.setMeta({ ...meta, user });
    }
    return store;
  }
  let opened = await loadStore(true);
  const stored = opened?.meta();
  if (stored && (stored.user.id !== user.id || stored.serverUrl !== serverUrl())) {
    // Another account's copy (FR17: the login page asked first if it had unsent changes).
    await wipeOfflineStore();
    opened = await loadStore(true);
  }
  if (!opened) throw new Error("No offline store");
  opened.setMeta({ serverUrl: serverUrl(), user, lastSync: stored?.lastSync ?? null });
  setStore(opened);
  return opened;
}

/** FR16: offline start with a token and a filled store; the user it belongs to. */
export async function offlineUser(): Promise<User | null> {
  if (!(await nativeTokens.get())) return null;
  const opened = store ?? (await loadStore(false));
  const meta = opened?.meta();
  if (!opened || !meta || meta.serverUrl !== serverUrl()) return null;
  setStore(opened);
  return meta.user;
}

/** FR15: sign-out and server switch delete the store, the outbox and the key. */
export async function wipeOfflineStore() {
  const current = store;
  setStore(null);
  if (current) await current.destroy();
  else await indexedDbPersistence().destroy();
  await deleteKey();
}

// --- Scheduling (FR7) ---

export type SyncEvent = SyncResult & { backOnline: boolean };

let running: Promise<SyncResult> | null = null;
let again = false;
let syncing = false;
let wasOffline = false;
let lastKind: SyncResult["kind"] | null = null;
const syncListeners = new Set<(event: SyncEvent) => void>();
const statusListeners = new Set<() => void>();

function setSyncing(next: boolean) {
  syncing = next;
  for (const listener of statusListeners) listener();
}

export const isSyncing = () => syncing;
export function subscribeSyncing(listener: () => void) {
  statusListeners.add(listener);
  return () => void statusListeners.delete(listener);
}

/** One sync now, or one more right after the running one. */
export function syncNow(): Promise<SyncResult> | null {
  const current = store;
  if (!current) return null;
  if (running) {
    again = true;
    return running;
  }
  const sync = createSync({ api, store: current, appVersion: APP_VERSION });
  setSyncing(true);
  running = (async () => {
    let result: SyncResult;
    do {
      again = false;
      result = await sync();
    } while (again && result.kind === "ok" && store === current);
    return result;
  })().finally(() => {
    running = null;
    setSyncing(false);
  });
  void running.then((result) => {
    lastKind = result.kind;
    if (result.kind === "offline") wasOffline = true;
    const backOnline = result.kind === "ok" && result.sent > 0 && wasOffline;
    if (result.kind === "ok") wasOffline = false;
    for (const listener of syncListeners) listener({ ...result, backOnline });
  });
  return running;
}

const LOCAL_CHANGE_DELAY_MS = 1000;
const INTERVAL_MS = 5 * 60_000;

/**
 * Syncs at start, when the connection or the app comes back, 1 s after local changes and
 * every 5 minutes in the foreground. Returns a stop function.
 */
export function startOfflineSync(onResult: (event: SyncEvent) => void): () => void {
  syncListeners.add(onResult);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const now = () => void syncNow();
  const visible = () => document.visibilityState === "visible";
  const onVisibility = () => visible() && now();
  const onOffline = () => {
    wasOffline = true;
  };
  // Local changes: only while the server is reachable (offline waits for `online`).
  let unsubscribeStore = () => {};
  const watchStore = () => {
    unsubscribeStore();
    unsubscribeStore =
      store?.subscribe(() => {
        if (running || lastKind === "offline" || store?.pendingEntries().length === 0) return;
        clearTimeout(timer);
        timer = setTimeout(now, LOCAL_CHANGE_DELAY_MS);
      }) ?? (() => {});
  };
  const unsubscribeStores = subscribeOfflineStore(watchStore);
  watchStore();
  const interval = setInterval(() => visible() && now(), INTERVAL_MS);
  window.addEventListener("online", now);
  window.addEventListener("offline", onOffline);
  document.addEventListener("visibilitychange", onVisibility);
  now();
  return () => {
    syncListeners.delete(onResult);
    clearTimeout(timer);
    clearInterval(interval);
    unsubscribeStore();
    unsubscribeStores();
    window.removeEventListener("online", now);
    window.removeEventListener("offline", onOffline);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
